<template>
  <div class="pc-report">
    <div class="rp-body">
      <div v-if="loading" class="rp-state">
        <span class="rp-spin"></span>
        <p>报告加载中…</p>
      </div>
      <div v-else-if="error" class="rp-state rp-error">
        <p>{{ error }}</p>
        <button class="rp-retry" @click="onRetry" :disabled="loading">重试</button>
      </div>
      <div v-else id="pc-pdf" class="rp-pdf"></div>
    </div>
  </div>
</template>

<script>
import pub from '@/service/public';
import Pdfh5 from 'pdfh5';
import 'pdfh5/css/pdfh5.css';
import config from '@/config';

export default {
  name: 'Report',
  data() {
    return {
      loading: false,
      error: '',
      pdfh5: null
    };
  },
  computed: {
    orderId() {
      return this.$route.query.id || '';
    }
  },
  mounted() {
    this.fetchReport();
  },
  watch: {
    '$route'() {
      if (this.pdfh5) {
        this.pdfh5.destroy();
        this.pdfh5 = null;
      }
      this.fetchReport();
    }
  },
  beforeDestroy() {
    if (this.pdfh5) {
      this.pdfh5.destroy();
      this.pdfh5 = null;
    }
  },
  methods: {
    // 手动重试时重置自动重试标记
    onRetry() {
      this._autoRetried = false;
      this.fetchReport();
    },
    async fetchReport() {
      const id = this.orderId;
      if (!id) {
        this.error = '订单ID不存在';
        return;
      }

      this.loading = true;
      this.error = '';

      try {
        // 免登录分享页首次打开时 config.json 可能尚未加载完，
        // 同步取值会拿到 undefined/旧地址导致跨域报错，必须先等配置就绪
        const cfg = await config.getConfig();
        const pdfApiRoot = cfg.PDF_API_ROOT || cfg.API_ROOT;
        if (!pdfApiRoot) {
          this.error = '服务配置缺失';
          this.loading = false;
          return;
        }
        const url = `${pdfApiRoot}/Record/GetReport?strOrderUUID=${encodeURIComponent(id)}`;

        const response = await fetch(url);
        if (!response.ok) {
          throw new Error('获取报告失败');
        }
        const data = await response.json();

        this.loading = false;
        if (data && data.code === pub.backCode.SUCCESS) {
          const list = data.data && data.data.pageList;
          if (list && list.length > 0) {
            const u = list[0].reportUrl;
            if (u) {
              this.initPdf(u);
            } else {
              this.error = '该记录暂无报告';
            }
          } else {
            this.error = '未找到该记录';
          }
        } else {
          this.error = (data && data.message) || '获取报告失败';
        }
      } catch (err) {
        this.loading = false;
        console.error('获取PDF失败：', err);
        // 首次加载偶发失败（如配置/网络时序），自动重试一次
        if (!this._autoRetried) {
          this._autoRetried = true;
          this.fetchReport();
          return;
        }
        this.error = '获取报告失败，请稍后重试';
      }
    },
    initPdf(url) {
      this.$nextTick(() => {
        const container = document.getElementById('pc-pdf');
        if (!container) return;

        // 按容器宽度估算 scale，让 PDF 页面宽度接近容器宽度，避免 CSS 放大过度模糊
        const fitScale = Math.max(1, Math.round(container.clientWidth / 800 * 10) / 10);

        this.pdfh5 = new Pdfh5('#pc-pdf', {
          pdfurl: url,
          renderType: 'canvas',
          lazyLoad: false,
          scrollEnable: true,
          zoomEnable: true,
          scale: fitScale,
          textLayer: false,
          cMapUrl: 'https://unpkg.com/pdfjs-dist@2.0.943/cmaps/',
          cMapPacked: true
        });

        this.pdfh5.on('complete', (status, msg, time) => {
          console.log('PDF加载完成，状态：' + status + '，总页数：' + this.pdfh5.totalNum);
          this.pdfh5.zoomEnable(true);
        });

        this.pdfh5.on('error', (msg, time) => {
          console.error('PDF加载错误：' + msg);
          this.error = '报告加载失败';
        });
      });
    }
  }
};
</script>

<style scoped>
.pc-report {
  --primary: #2c9b86;
  height: 100vh;
  background: #f5fbfe;
  display: flex;
  flex-direction: column;
  font-family: -apple-system, BlinkMacSystemFont, "PingFang SC", "Helvetica Neue", "Microsoft YaHei", sans-serif;
}

/* 主体 */
.rp-body {
  flex: 1;
  min-height: 0;
  position: relative;
  padding: 0;
}

/* PDF 满屏，无导航栏，不限制圆角边距 */
.rp-pdf {
  position: absolute;
  inset: 0;
  overflow-y: auto;
}

/* 让 PDF 单页宽度铺满容器，高度自适应，并去除 pdfh5 默认页边距 */
.rp-pdf .page {
  width: 100% !important;
  height: auto !important;
  margin: 0 !important;
  padding: 0 !important;
}

.rp-pdf .canvasWrapper,
.rp-pdf canvas {
  width: 100% !important;
  height: auto !important;
}

/* 加载 / 错误态 */
.rp-state {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  color: #6f8c83;
}

.rp-state p {
  font-size: 14px;
  margin: 0;
}

.rp-spin {
  width: 30px;
  height: 30px;
  border: 3px solid #d4e6e1;
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: rp-spin .8s linear infinite;
}

@keyframes rp-spin {
  to {
    transform: rotate(360deg)
  }
}

.rp-error p {
  color: #2b4d43;
}

.rp-retry {
  height: 38px;
  padding: 0 22px;
  border-radius: 19px;
  border: none;
  background: var(--primary);
  color: #fff;
  font-size: 14px;
  cursor: pointer;
}

.rp-retry:hover {
  background: #20816e;
}
</style>



// WEBPACK FOOTER //
// src/components/Report.vue